import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Shield, Mail, Calendar, ChevronUp } from 'lucide-react';
import { LOGO_URL, BrandText } from '@/lib/brand';

const sections = [
  { id: 'collection', title: '1. Information We Collect' },
  { id: 'usage', title: '2. How We Use Your Data' },
  { id: 'security', title: '3. Data Security' },
  { id: 'sharing', title: '4. Third-Party Services' },
  { id: 'rights', title: '5. Your Rights (DPDP Act)' },
  { id: 'retention', title: '6. Data Retention' },
  { id: 'cookies', title: '7. Cookies & Tracking' },
  { id: 'children', title: '8. Children\'s Privacy' },
  { id: 'changes', title: '9. Policy Updates' },
  { id: 'contact', title: '10. Contact Us' },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-border/40" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(248,250,252,0.97) 100%)', backdropFilter: 'blur(20px) saturate(180%)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Out-Play" className="h-9 w-9 rounded-xl object-cover" />
            <BrandText className="text-xl tracking-tight" />
          </Link>
          <Link to="/"><Button variant="ghost" size="sm" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back to Home</Button></Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.1),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 text-center">
          <Shield className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-white mb-3">Privacy Policy</h1>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">How we collect, use, and protect your data at Out-Play.in</p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30"><Calendar className="h-3 w-3 mr-1" /> Last Updated: February 19, 2026</Badge>
            <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">DPDP Act Compliant</Badge>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Table of Contents - Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Sections</p>
              {sections.map(s => (
                <a key={s.id} href={`#${s.id}`} className="block text-sm text-muted-foreground hover:text-foreground py-1 transition-colors">{s.title}</a>
              ))}
            </div>
          </aside>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-10">
            {/* Intro */}
            <div className="p-6 rounded-2xl bg-card border shadow-sm">
              <p className="text-base leading-relaxed text-muted-foreground">
                Welcome to <strong className="text-foreground">Out-Play.in</strong> ("we", "our", or "us"). We are committed to protecting the privacy of academy owners, coaches, students, parents, and all users of our sports academy management platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information in compliance with the <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> of India and applicable Google Play Store policies.
              </p>
            </div>

            {/* Section 1 */}
            <div id="collection">
              <h2 className="text-xl font-display font-bold mb-4">1. Information We Collect</h2>
              <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
                <p>We collect data necessary to provide our sports academy management services:</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                    <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 mb-2">Personal Information</h4>
                    <ul className="text-sm space-y-1"><li>Full name & date of birth</li><li>Email address & phone number</li><li>Guardian/parent details</li><li>Profile photographs</li><li>Physical metrics (height, weight)</li></ul>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                    <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-2">Sports & Academy Data</h4>
                    <ul className="text-sm space-y-1"><li>Attendance records</li><li>Skill ratings & progress cards</li><li>Batch & course enrollment</li><li>Event participation</li><li>Coach feedback & assessments</li></ul>
                  </div>
                  <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                    <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">Financial Records</h4>
                    <ul className="text-sm space-y-1"><li>Fee payment history</li><li>Invoice & subscription data</li><li>Razorpay transaction IDs</li><li>UPI/card payment references</li><li>Payroll information (staff)</li></ul>
                  </div>
                </div>
              </div>
            </div>
            <Separator />

            {/* Section 2 */}
            <div id="usage">
              <h2 className="text-xl font-display font-bold mb-4">2. How We Use Your Data</h2>
              <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                <p>Your data is used exclusively to provide and improve our academy management services:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong className="text-foreground">Academy Operations:</strong> Student enrollment, attendance tracking, fee management, event coordination, and progress reporting.</li>
                  <li><strong className="text-foreground">Communication:</strong> Sending fee reminders, event notifications, progress updates, and system alerts via email, SMS, or WhatsApp.</li>
                  <li><strong className="text-foreground">Analytics & AI:</strong> Generating revenue forecasts, attendance insights, duplicate invoice detection, and performance recommendations for academy administrators.</li>
                  <li><strong className="text-foreground">Platform Improvement:</strong> Analyzing usage patterns to improve features, fix bugs, and optimize performance.</li>
                  <li><strong className="text-foreground">Legal Compliance:</strong> GST invoice generation, payroll tax calculations, and regulatory record-keeping.</li>
                </ul>
              </div>
            </div>
            <Separator />

            {/* Section 3 */}
            <div id="security">
              <h2 className="text-xl font-display font-bold mb-4">3. Data Security</h2>
              <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                <p>We implement industry-standard security measures to protect your data:</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="p-4 rounded-xl border bg-card">
                    <h4 className="text-sm font-semibold text-foreground mb-1">Authentication & Encryption</h4>
                    <p className="text-sm">JWT (JSON Web Token) authentication with bcrypt password hashing. All data transmitted over HTTPS with TLS 1.3 encryption.</p>
                  </div>
                  <div className="p-4 rounded-xl border bg-card">
                    <h4 className="text-sm font-semibold text-foreground mb-1">Multi-Tenant Isolation</h4>
                    <p className="text-sm">Each academy's data is isolated using MongoDB document-level security with unique academy_id references. No cross-academy data access.</p>
                  </div>
                  <div className="p-4 rounded-xl border bg-card">
                    <h4 className="text-sm font-semibold text-foreground mb-1">Role-Based Access Control</h4>
                    <p className="text-sm">Five distinct user roles (Super Admin, Academy Owner, Coach, Student, Parent) with permission-based data access.</p>
                  </div>
                  <div className="p-4 rounded-xl border bg-card">
                    <h4 className="text-sm font-semibold text-foreground mb-1">Secure Payments</h4>
                    <p className="text-sm">Payment processing via Razorpay's PCI-DSS compliant infrastructure. We never store card numbers or CVVs.</p>
                  </div>
                </div>
              </div>
            </div>
            <Separator />

            {/* Section 4 */}
            <div id="sharing">
              <h2 className="text-xl font-display font-bold mb-4">4. Third-Party Services</h2>
              <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                <p>We integrate with trusted third-party providers to deliver our services:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong className="text-foreground">Razorpay</strong> — Payment processing (UPI, cards, net banking). Subject to <a href="https://razorpay.com/privacy/" className="text-primary hover:underline" target="_blank" rel="noreferrer">Razorpay Privacy Policy</a>.</li>
                  <li><strong className="text-foreground">MongoDB Atlas</strong> — Cloud database storage with enterprise-grade encryption at rest.</li>
                  <li><strong className="text-foreground">Resend</strong> — Transactional email delivery for invoices, reminders, and notifications.</li>
                  <li><strong className="text-foreground">Twilio</strong> — SMS and WhatsApp messaging for fee reminders and event notifications.</li>
                </ul>
                <p>We do not sell, rent, or trade your personal data to any third party for marketing purposes.</p>
              </div>
            </div>
            <Separator />

            {/* Section 5 */}
            <div id="rights">
              <h2 className="text-xl font-display font-bold mb-4">5. Your Rights Under the DPDP Act</h2>
              <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                <p>Under India's Digital Personal Data Protection Act, 2023, you have the following rights:</p>
                <div className="space-y-3">
                  <div className="p-4 rounded-xl border bg-card">
                    <h4 className="text-sm font-semibold text-foreground">Right to Access</h4>
                    <p className="text-sm">Request a copy of all personal data we hold about you. Available via your dashboard or by emailing us.</p>
                  </div>
                  <div className="p-4 rounded-xl border bg-card">
                    <h4 className="text-sm font-semibold text-foreground">Right to Correction</h4>
                    <p className="text-sm">Update or correct inaccurate personal information through your profile settings or by contacting support.</p>
                  </div>
                  <div className="p-4 rounded-xl border bg-card">
                    <h4 className="text-sm font-semibold text-foreground">Right to Erasure</h4>
                    <p className="text-sm">Request deletion of your account and associated data. We will comply within 30 days, except where retention is required by law.</p>
                  </div>
                  <div className="p-4 rounded-xl border bg-card">
                    <h4 className="text-sm font-semibold text-foreground">Right to Withdraw Consent</h4>
                    <p className="text-sm">Withdraw consent for data processing at any time. This may limit your ability to use certain platform features.</p>
                  </div>
                  <div className="p-4 rounded-xl border bg-card">
                    <h4 className="text-sm font-semibold text-foreground">Right to Grievance Redressal</h4>
                    <p className="text-sm">File a complaint with our Data Protection Officer or the Data Protection Board of India.</p>
                  </div>
                </div>
              </div>
            </div>
            <Separator />

            {/* Section 6 */}
            <div id="retention">
              <h2 className="text-xl font-display font-bold mb-4">6. Data Retention</h2>
              <div className="text-base text-muted-foreground leading-relaxed space-y-3">
                <p>We retain your data only as long as necessary for the purposes described in this policy:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong className="text-foreground">Active accounts:</strong> Data is retained for the duration of your subscription.</li>
                  <li><strong className="text-foreground">After account deletion:</strong> Personal data is deleted within 30 days. Financial records (invoices, tax data) may be retained for up to 8 years as required by Indian tax law.</li>
                  <li><strong className="text-foreground">Backups:</strong> Encrypted backups are purged within 90 days of deletion.</li>
                </ul>
              </div>
            </div>
            <Separator />

            {/* Section 7 */}
            <div id="cookies">
              <h2 className="text-xl font-display font-bold mb-4">7. Cookies & Tracking</h2>
              <div className="text-base text-muted-foreground leading-relaxed space-y-3">
                <p>We use minimal cookies and local storage for essential functionality:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong className="text-foreground">Authentication tokens:</strong> Stored in localStorage to maintain your login session.</li>
                  <li><strong className="text-foreground">Theme preferences:</strong> Light/dark mode setting stored locally.</li>
                </ul>
                <p>We do not use third-party tracking cookies, advertising cookies, or behavioral analytics tools that track individual users across websites.</p>
              </div>
            </div>
            <Separator />

            {/* Section 8 */}
            <div id="children">
              <h2 className="text-xl font-display font-bold mb-4">8. Children's Privacy</h2>
              <div className="text-base text-muted-foreground leading-relaxed space-y-3">
                <p>Our platform manages data for students of all ages, including minors under 18. For students under 18:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Account creation and data processing requires <strong className="text-foreground">parental/guardian consent</strong>.</li>
                  <li>Guardian contact information is collected alongside student profiles.</li>
                  <li>Parents/guardians may request access to, correction of, or deletion of their child's data at any time.</li>
                </ul>
              </div>
            </div>
            <Separator />

            {/* Section 9 */}
            <div id="changes">
              <h2 className="text-xl font-display font-bold mb-4">9. Policy Updates</h2>
              <div className="text-base text-muted-foreground leading-relaxed">
                <p>We may update this Privacy Policy periodically. When we make material changes, we will notify users via in-app notification and email. The "Last Updated" date at the top of this page indicates the most recent revision. Continued use of our platform after changes constitutes acceptance of the updated policy.</p>
              </div>
            </div>
            <Separator />

            {/* Section 10 */}
            <div id="contact">
              <h2 className="text-xl font-display font-bold mb-4">10. Contact Us</h2>
              <div className="p-6 rounded-2xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                <p className="text-base text-muted-foreground leading-relaxed mb-4">For privacy-related inquiries, data access requests, or complaints, please contact our Data Protection Officer:</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-base"><Mail className="h-5 w-5 text-emerald-600" /><a href="mailto:contact@out-play.in" className="text-primary font-semibold hover:underline">contact@out-play.in</a></div>
                  <p className="text-sm text-muted-foreground">Out-Play.in | Sports Academy Management Platform</p>
                  <p className="text-sm text-muted-foreground">We aim to respond to all privacy requests within 72 hours.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to top */}
      <div className="text-center py-8">
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"><ChevronUp className="h-4 w-4" /> Back to top</button>
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-card py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="flex items-center gap-2"><img src={LOGO_URL} alt="Out-Play" className="h-6 w-6 rounded object-cover" /><BrandText className="text-sm" /><span className="text-xs text-muted-foreground">&copy; 2026</span></div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            <span className="text-foreground font-medium">Privacy Policy</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
